import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPlus, FiSearch, FiMoreVertical, FiEdit2, FiTrash2, FiUser,
  FiCheck, FiX, FiAlertCircle, FiToggleLeft, FiToggleRight,
  FiMail, FiClock, FiRefreshCw, FiArrowUpCircle, FiUsers,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import * as usersApi from '../../api/users';
import * as invitationsApi from '../../api/invitations';
import type { SeatUsage, Invitation } from '../../api/invitations';
import { useAuth } from '../../context/AuthContext';

type OrgUser = usersApi.OrgUser;

const ROLE_LABELS: Record<string, string> = {
  owner:  'Propriétaire',
  admin:  'Admin',
  member: 'Membre',
  viewer: 'Lecteur',
};

const ROLE_COLORS: Record<string, string> = {
  owner:  'bg-purple-100 text-purple-700',
  admin:  'bg-blue-100 text-blue-700',
  member: 'bg-green-100 text-green-700',
  viewer: 'bg-gray-100 text-gray-600',
};

// ── Quota Banner ─────────────────────────────────────────────────────────────

const QuotaBanner: React.FC<{ quota: SeatUsage; onInvite: () => void; canManage: boolean }> = ({
  quota, onInvite, canManage,
}) => {
  const pct = quota.member_max_total
    ? Math.min(100, (quota.occupied / quota.member_max_total) * 100)
    : 0;

  const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-orange-400' : 'bg-blue-500';
  const textColor = pct >= 100 ? 'text-red-700' : pct >= 80 ? 'text-orange-700' : 'text-blue-700';

  const fmtPrice = (cents: number) => `${(cents / 100).toFixed(0)}€`;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <FiUsers size={15} className="text-gray-400" />
            Sièges membres
          </span>
          <span className={`text-sm font-semibold ${textColor}`}>
            {quota.occupied}
            {quota.member_max_total ? ` / ${quota.member_max_total}` : ''} occupés
          </span>
        </div>
        {quota.member_max_total && (
          <div className="w-full bg-gray-100 rounded-full h-2 mb-2">
            <div className={`${barColor} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
          </div>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
          <span>{quota.included_seats} inclus dans le plan <strong className="text-gray-700 uppercase">{quota.plan}</strong></span>
          {quota.additional_seats_used > 0 && quota.addon_price_cents && (
            <span className="text-orange-600">
              +{quota.additional_seats_used} siège{quota.additional_seats_used > 1 ? 's' : ''} additionnel{quota.additional_seats_used > 1 ? 's' : ''} ({fmtPrice(quota.addon_price_cents)}/siège/mois)
            </span>
          )}
          {quota.monthly_total_cents > 0 && (
            <span className="text-gray-600">Total mensuel : <strong className="text-gray-800">{fmtPrice(quota.monthly_total_cents)}</strong></span>
          )}
        </div>
      </div>
      {canManage && (
        <button
          onClick={onInvite}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium shrink-0"
        >
          <FiPlus size={16} />
          Inviter un membre
        </button>
      )}
    </div>
  );
};

// ── Invite Modal (3 états) ────────────────────────────────────────────────────

type InviteModalStep = 'form' | 'addon_confirm' | 'upgrade_wall';

interface AddonInfo {
  addon_price_cents: number;
  new_total_cents: number;
  message: string;
  usage: SeatUsage;
}

const InviteModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
}> = ({ onClose, onSuccess }) => {
  const [step, setStep]           = useState<InviteModalStep>('form');
  const [email, setEmail]         = useState('');
  const [role, setRole]           = useState('member');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [addonInfo, setAddonInfo] = useState<AddonInfo | null>(null);

  const fmtPrice = (cents: number) => `${(cents / 100).toFixed(0)}€`;

  const submit = async (confirmAddon = false) => {
    setLoading(true);
    setError(null);
    try {
      await invitationsApi.createInvitation({ email, role, confirm_addon: confirmAddon });
      onSuccess();
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.error === 'SEAT_ADDON_AVAILABLE') {
        setAddonInfo({
          addon_price_cents: data.addon_price_cents,
          new_total_cents:   data.new_total_cents,
          message:           data.message,
          usage:             data.usage,
        });
        setStep('addon_confirm');
      } else if (data?.error === 'PLAN_UPGRADE_REQUIRED') {
        setStep('upgrade_wall');
      } else {
        setError(data?.error || data?.message || 'Erreur lors de l\'envoi de l\'invitation');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">

        {/* Form step */}
        {step === 'form' && (
          <>
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Inviter un membre</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><FiX size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-500">
                Un lien d'invitation sera envoyé par email. Le destinataire choisira lui-même son mot de passe.
              </p>
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
                  <FiAlertCircle size={16} />
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="prenom.nom@entreprise.com"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                <select
                  value={role}
                  onChange={e => setRole(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="admin">Admin — accès complet (sauf facturation)</option>
                  <option value="member">Membre — lecture + création</option>
                  <option value="viewer">Lecteur — lecture seule</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Annuler</button>
                <button
                  type="button"
                  onClick={() => submit(false)}
                  disabled={loading || !email}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiMail size={16} />}
                  Envoyer l'invitation
                </button>
              </div>
            </div>
          </>
        )}

        {/* Addon confirmation step */}
        {step === 'addon_confirm' && addonInfo && (
          <>
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Confirmer le siège supplémentaire</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><FiX size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-orange-800 font-medium text-sm mb-3">{addonInfo.message}</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Sièges additionnels actuels</span>
                    <span>{addonInfo.usage.additional_seats_used}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Coût par siège supplémentaire</span>
                    <span>{fmtPrice(addonInfo.addon_price_cents)}/mois</span>
                  </div>
                  <hr className="my-2 border-orange-200" />
                  <div className="flex justify-between font-semibold text-gray-800">
                    <span>Nouveau total mensuel</span>
                    <span>{fmtPrice(addonInfo.new_total_cents)}</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500">La différence sera facturée au prorata sur votre prochaine facture.</p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setStep('form')} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Retour</button>
                <button
                  type="button"
                  onClick={() => submit(true)}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
                  Confirmer et inviter
                </button>
              </div>
            </div>
          </>
        )}

        {/* Upgrade wall step */}
        {step === 'upgrade_wall' && (
          <>
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Limite atteinte</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><FiX size={20} /></button>
            </div>
            <div className="p-6 text-center space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <FiArrowUpCircle size={32} className="text-red-500" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 mb-1">Nombre maximum de membres atteint</p>
                <p className="text-sm text-gray-500">Votre plan ne permet pas d'ajouter de nouveaux membres. Passez au plan supérieur pour continuer à développer votre équipe.</p>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Fermer</button>
                <a
                  href="/settings/subscription"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-center font-medium"
                >
                  Voir les plans
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Edit Role Modal ───────────────────────────────────────────────────────────

const EditRoleModal: React.FC<{
  user: OrgUser;
  onClose: () => void;
  onSave: (role: string) => void;
}> = ({ user, onClose, onSave }) => {
  const [role, setRole] = useState(user.role);
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Modifier le rôle</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><FiX size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            <strong>{user.fullName}</strong> ({user.email})
          </p>
          <select
            value={role}
            onChange={e => setRole(e.target.value as OrgUser['role'])}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="admin">Admin</option>
            <option value="member">Membre</option>
            <option value="viewer">Lecteur</option>
          </select>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Annuler</button>
            <button type="button" onClick={() => onSave(role)} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Enregistrer</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const UsersPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const { user: currentUser } = useAuth();

  const [users, setUsers]             = useState<OrgUser[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [quota, setQuota]             = useState<SeatUsage | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [searchTerm, setSearchTerm]   = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingUser, setEditingUser] = useState<OrgUser | null>(null);
  const [activeMenu, setActiveMenu]   = useState<string | null>(null);
  const [toast, setToast]             = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const canManage = currentUser?.role === 'owner' || currentUser?.role === 'admin';
  const canDelete = currentUser?.role === 'owner';

  const showToast = useCallback((type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [userData, invData, quotaData] = await Promise.all([
        usersApi.listUsers(),
        canManage ? invitationsApi.listInvitations() : Promise.resolve([]),
        canManage ? invitationsApi.getUserQuota()     : Promise.resolve(null),
      ]);
      setUsers(userData);
      setInvitations(invData);
      setQuota(quotaData);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Impossible de charger les membres');
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = () => setActiveMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const handleToggleActive = async (user: OrgUser) => {
    if (!canManage) return;
    try {
      await usersApi.updateUser(user.id, { isActive: !user.isActive });
      showToast('success', `Utilisateur ${user.isActive ? 'désactivé' : 'activé'}`);
      loadAll();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Erreur lors de la mise à jour');
    }
    setActiveMenu(null);
  };

  const handleDelete = async (user: OrgUser) => {
    if (!canDelete) return;
    if (!window.confirm(`Supprimer ${user.fullName} (${user.email}) ? Cette action est irréversible.`)) return;
    try {
      await usersApi.deleteUser(user.id);
      showToast('success', 'Utilisateur supprimé');
      loadAll();
    } catch (e: any) {
      const msg = e.response?.data?.message || e.response?.data?.error || 'Erreur lors de la suppression';
      showToast('error', msg);
    }
    setActiveMenu(null);
  };

  const handleEditRole = async (role: string) => {
    if (!editingUser) return;
    try {
      await usersApi.updateUser(editingUser.id, { role });
      showToast('success', 'Rôle mis à jour');
      setEditingUser(null);
      loadAll();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Erreur');
    }
  };

  const handleCancelInvitation = async (id: string) => {
    try {
      await invitationsApi.cancelInvitation(id);
      showToast('success', 'Invitation annulée');
      loadAll();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Erreur');
    }
    setActiveMenu(null);
  };

  const handleResendInvitation = async (id: string) => {
    try {
      await invitationsApi.resendInvitation(id);
      showToast('success', 'Invitation renvoyée');
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Erreur');
    }
    setActiveMenu(null);
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const filteredUsers = users.filter(u =>
    u.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredInvitations = invitations.filter(i =>
    i.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalRows = filteredUsers.length + filteredInvitations.length;

  return (
    <div className="p-8">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {toast.type === 'success' ? <FiCheck size={16} /> : <FiAlertCircle size={16} />}
          {toast.msg}
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{t('users.header.title', 'Membres')}</h2>
        <p className="text-gray-600 mt-1">{t('users.header.subtitle', 'Gérez les membres et les invitations de votre organisation.')}</p>
      </div>

      {/* Quota banner */}
      {canManage && quota && (
        <QuotaBanner
          quota={quota}
          onInvite={() => setShowInviteModal(true)}
          canManage={canManage}
        />
      )}

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Rechercher un membre ou une invitation..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <FiAlertCircle size={32} className="mx-auto text-red-400 mb-2" />
          <p className="text-red-700">{error}</p>
          <button onClick={loadAll} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">Réessayer</button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase">
            {totalRows} entrée{totalRows !== 1 ? 's' : ''}
            {filteredInvitations.length > 0 && ` (dont ${filteredInvitations.length} invitation${filteredInvitations.length > 1 ? 's' : ''} en attente)`}
          </div>

          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernière connexion</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Membre depuis</th>
                {canManage && <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {totalRows === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <FiUser size={48} className="mx-auto text-gray-300 mb-3" />
                    <p>Aucun membre trouvé</p>
                  </td>
                </tr>
              ) : (
                <>
                  {/* Active/inactive members */}
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className={`hover:bg-gray-50 ${!user.isActive ? 'opacity-60' : ''}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm shrink-0">
                            {user.fullName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 flex items-center gap-1.5 text-sm">
                              {user.fullName}
                              {user.id === currentUser?.id && <span className="text-xs text-blue-600 font-normal">(vous)</span>}
                            </div>
                            <div className="text-xs text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[user.role] || 'bg-gray-100 text-gray-700'}`}>
                          {ROLE_LABELS[user.role] || user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${user.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${user.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                          {user.isActive ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(user.lastLogin)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(user.createdAt)}</td>
                      {canManage && (
                        <td className="px-6 py-4 text-right">
                          <div className="relative inline-block" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => setActiveMenu(activeMenu === user.id ? null : user.id)}
                              className="p-2 hover:bg-gray-100 rounded-full"
                            >
                              <FiMoreVertical className="text-gray-500" size={16} />
                            </button>
                            {activeMenu === user.id && (
                              <div className="absolute right-0 mt-1 w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
                                {user.role !== 'owner' && (
                                  <button
                                    onClick={() => { setEditingUser(user); setActiveMenu(null); }}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 text-sm"
                                  >
                                    <FiEdit2 size={14} /> Modifier le rôle
                                  </button>
                                )}
                                {user.id !== currentUser?.id && (
                                  <button
                                    onClick={() => handleToggleActive(user)}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 text-sm"
                                  >
                                    {user.isActive ? <FiToggleLeft size={14} /> : <FiToggleRight size={14} />}
                                    {user.isActive ? 'Désactiver' : 'Activer'}
                                  </button>
                                )}
                                {canDelete && user.id !== currentUser?.id && user.role !== 'owner' && (
                                  <>
                                    <hr className="my-1" />
                                    <button
                                      onClick={() => handleDelete(user)}
                                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-red-600 text-sm"
                                    >
                                      <FiTrash2 size={14} /> Retirer de l'organisation
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}

                  {/* Pending invitations */}
                  {filteredInvitations.map((inv) => (
                    <tr key={`inv-${inv.id}`} className="hover:bg-gray-50 bg-amber-50/40">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                            <FiMail size={16} className="text-amber-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-700 text-sm">{inv.email}</div>
                            <div className="text-xs text-gray-400">
                              {inv.invited_by_name ? `Invité par ${inv.invited_by_name}` : 'Invitation envoyée'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[inv.role] || 'bg-gray-100 text-gray-700'}`}>
                          {ROLE_LABELS[inv.role] || inv.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          <FiClock size={11} />
                          En attente
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-400">
                        Expire le {formatDate(inv.expires_at)}
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-400">
                        {formatDate(inv.created_at)}
                      </td>
                      {canManage && (
                        <td className="px-6 py-4 text-right">
                          <div className="relative inline-block" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => setActiveMenu(activeMenu === `inv-${inv.id}` ? null : `inv-${inv.id}`)}
                              className="p-2 hover:bg-gray-100 rounded-full"
                            >
                              <FiMoreVertical className="text-gray-500" size={16} />
                            </button>
                            {activeMenu === `inv-${inv.id}` && (
                              <div className="absolute right-0 mt-1 w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
                                <button
                                  onClick={() => handleResendInvitation(inv.id)}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 text-sm"
                                >
                                  <FiRefreshCw size={14} /> Renvoyer l'invitation
                                </button>
                                <hr className="my-1" />
                                <button
                                  onClick={() => handleCancelInvitation(inv.id)}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-red-600 text-sm"
                                >
                                  <FiX size={14} /> Annuler l'invitation
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite modal */}
      {showInviteModal && (
        <InviteModal
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            showToast('success', 'Invitation envoyée avec succès');
            loadAll();
          }}
        />
      )}

      {/* Edit role modal */}
      {editingUser && (
        <EditRoleModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleEditRole}
        />
      )}
    </div>
  );
};

export default UsersPage;
